import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment';

const OtpView = () => {
    // State Management
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // Pagination & Counts
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // Statistics Counts
    const [counts, setCounts] = useState({
        total: 0,
        approved: 0,
        rejected: 0,
        pending: 0
    });

    // 1. Data Fetching Function
    const fetchOtpMessages = useCallback(() => {
        setLoading(true);
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {}; 
        const allowedUsers = JSON.parse(localStorage.getItem('allowedUsers')) || [];
        const isMaster = localStorage.getItem('isMaster'); 

        // ব্যাকএন্ডে সকেট রিকোয়েস্ট পাঠানো হচ্ছে
        socketService.emit('fetch_otp_messages', {
            page: currentPage,
            search: searchTerm,
            startDate: startDate,
            endDate: endDate,
            isMaster: isMaster, 
            allowedUsers: allowedUsers, 
            currentUsername: userInfo.username 
        });
    }, [currentPage, searchTerm, startDate, endDate]);

    // 2. Socket Listeners
    useEffect(() => {
        socketService.connect();

        const handleDataReceive = (data) => {
            setMessages(data.messages || []);
            setTotalPages(data.totalPages || 1);
            
            // ডাটাবেস থেকে আসা কাউন্ট আপডেট করা
            setCounts({
                total: data.totalRows || 0,
                approved: data.approvedCount || 0,
                rejected: data.rejectedCount || 0,
                pending: data.pendingCount || 0
            });
            
            setLoading(false);
        };

        socketService.on('receive_otp_messages', handleDataReceive);
        
        // Initial Fetch
        fetchOtpMessages();

        return () => {
            socketService.off('receive_otp_messages', handleDataReceive);
        };
    }, [fetchOtpMessages]);

    // 3. Handlers
    const handleSearch = () => {
        setCurrentPage(1);
        fetchOtpMessages();
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
        setTimeout(() => fetchOtpMessages(), 0);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisibleButtons = 10; 
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button className="page-link shadow-none" onClick={() => handlePageChange(i)}>{i}</button>
                </li>
            );
        }
        return pages;
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                {/* Header Section with Status Badges */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">📱 OTP Messages Monitor</h5>
                    <div className="d-flex gap-2">
                        <div className="count-badge shadow-sm" style={{borderColor: '#e2e8f0'}}>
                            Total: <span style={{color: '#334155'}}>{counts.total}</span>
                        </div>
                        <div className="count-badge shadow-sm" style={{borderColor: '#bbf7d0', backgroundColor: '#f0fdf4'}}>
                            Approved: <span style={{color: '#16a34a'}}>{counts.approved}</span>
                        </div>
                        <div className="count-badge shadow-sm" style={{borderColor: '#fecdd3', backgroundColor: '#fff1f2'}}>
                            Rejected: <span style={{color: '#e11d48'}}>{counts.rejected}</span>
                        </div>
                        <div className="count-badge shadow-sm" style={{borderColor: '#fef08a', backgroundColor: '#fefce8'}}>
                            Pending: <span style={{color: '#ca8a04'}}>{counts.pending}</span>
                        </div>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="filter-card shadow-sm border-0 mb-4">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                            <label className="field-label">SEARCH BY NUMBER / USER / MSG</label>
                            <input 
                                type="text" 
                                className="modern-input" 
                                placeholder="Type Phone Number, Username..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="field-label">FROM DATE</label>
                            <input 
                                type="date" 
                                className="modern-input" 
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="field-label">TO DATE</label>
                            <input 
                                type="date" 
                                className="modern-input" 
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2 d-flex gap-1">
                            <button className="btn btn-apply fw-bold w-100" onClick={handleSearch}>
                                Filter
                            </button>
                            <button className="btn btn-danger fw-bold w-100" style={{fontSize:'12px', padding: '8px 0'}} onClick={handleClearFilters}>
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-container shadow-sm border-0 mb-3">
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4" style={{width: '20%'}}>USER INFO</th>
                                    <th style={{width: '40%'}}>MESSAGE DETAILS</th>
                                    <th style={{width: '15%'}}>STATUS</th>
                                    <th className="text-end pe-4" style={{width: '25%'}}>TIMESTAMP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="4" className="text-center py-5">Loading Messages...</td></tr>
                                ) : messages.length === 0 ? (
                                    <tr><td colSpan="4" className="text-center py-5 opacity-50">No Data Found</td></tr>
                                ) : (
                                    messages.map((msg) => {
                                        
                                        // স্ট্যাটাস অনুযায়ী ডিজাইন পরিবর্তন
                                        let rowStyle = { verticalAlign: 'middle' };
                                        let badgeClass = "badge bg-secondary";
                                        let bgMuted = "#e2e8f0";

                                        if (msg.status === 'approved') {
                                            rowStyle = { ...rowStyle, backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e' };
                                            badgeClass = "badge bg-success";
                                            bgMuted = "#dcfce7";
                                        } else if (msg.status === 'rejected') {
                                            rowStyle = { ...rowStyle, backgroundColor: '#fff1f2', borderLeft: '4px solid #f43f5e' };
                                            badgeClass = "badge bg-danger";
                                            bgMuted = "#fee2e2";
                                        } else if (msg.status === 'pending') {
                                            rowStyle = { ...rowStyle, backgroundColor: '#fefce8', borderLeft: '4px solid #eab308' };
                                            badgeClass = "badge bg-warning text-dark";
                                            bgMuted = "#fef9c3";
                                        }

                                        return (
                                            <tr key={msg._id} style={rowStyle}>
                                                <td className="ps-4 pt-3 pb-3">
                                                    <div className="user-primary">{msg.username}</div>
                                                    <div className="device-secondary text-uppercase mt-1" style={{fontSize: '10px', letterSpacing: '0.5px', background: bgMuted, padding: '2px 6px', borderRadius: '4px', display:'inline-block'}}>
                                                        CD: <span style={{fontFamily: 'monospace'}}>{msg.cd || 'N/A'}</span>
                                                    </div>
                                                    <div className="mt-2 text-muted" style={{fontSize: '10px'}}>
                                                        ID: #{msg._id.slice(-6).toUpperCase()}
                                                    </div>
                                                </td>

                                                <td className="pt-3 pb-3">
                                                    <div className="mb-1" style={{fontSize: '14px', fontWeight: 'bold', color: '#1e293b'}}>
                                                        📞 {msg.number}
                                                    </div>
                                                    <div style={{fontSize: '13px', color: '#475569', backgroundColor: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0'}}>
                                                        {msg.message}
                                                    </div>
                                                </td>

                                                <td className="pt-3 pb-3">
                                                    <span className={badgeClass} style={{fontSize: '11px', padding: '5px 10px'}}>
                                                        {msg.status ? msg.status.toUpperCase() : 'UNKNOWN'}
                                                    </span>
                                                </td>

                                                <td className="text-end pe-4 pt-3 pb-3">
                                                    <div className="date-bold" style={{color: '#334155', fontWeight: 'bold', fontSize: '13px'}}>
                                                        {moment(msg.timestamp).format('DD MMM YYYY')}
                                                    </div>
                                                    <div className="time-muted" style={{fontSize: '11px', color: '#64748b'}}>
                                                        {moment(msg.timestamp).format('hh:mm:ss A')}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center px-2 py-2">
                    <div className="text-muted small">
                        Showing Page <b>{currentPage}</b> of <b>{totalPages}</b>
                    </div>
                    <nav>
                        <ul className="pagination pagination-sm m-0 gap-1">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(currentPage - 1)}>Prev</button>
                            </li>
                            {renderPageNumbers()}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                
                .filter-card { background: #ffffff; padding: 15px; border-radius: 12px; }
                .field-label { font-size: 10px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; display: block; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 7px 10px; border-radius: 8px; font-size: 12px; width: 100%; outline: none; }
                
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 12px; transition: 0.3s; border: none; padding: 8px 0; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #f1f5f9; color: #475569; border-bottom: 2px solid #e2e8f0; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; text-align: left; }
                
                .custom-zebra-table td { border-bottom: 1px solid #edf2f7; }
                
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; text-transform: capitalize; }
                
                .page-link { color: #475569; border: 1px solid #e2e8f0; border-radius: 6px !important; padding: 5px 12px; font-weight: 600; background: white; }
                .page-item.active .page-link { background-color: #0284c7; border-color: #0284c7; color: white; }
            `}</style>
        </Layout>
    );
};

export default OtpView;